import type {
  DatedRoleInput,
  MatrixData,
  MatrixDiffData,
  MatrixImportPreview,
  MatrixSaved,
  MatrixSubjects,
  MatrixVersionData,
  MatrixVersionItem,
  NewUserInput,
  OfficeUserItem,
  OfficeUserSaved,
  RoleInput,
  RoleItem,
  RoleList,
  SaveMatrixInput,
  SetupStatus,
  TemporaryPasswordResult,
  UserList,
  VesselAssignmentInput,
  VesselOption,
} from './setup-types.js';

/**
 * Şirket kurulumu ekranlarının veri kaynağı (ADR 0042 §22–§24): web ofiste API
 * (`apps/web/src/setup-source.ts`). Hatalar `code` taşır (`errorCode`).
 */
export interface SetupSource {
  setupStatus(): Promise<SetupStatus>;
  vessels(): Promise<VesselOption[]>;
  roles(): Promise<RoleList>;
  createRole(input: RoleInput): Promise<RoleItem>;
  updateRole(id: string, rowVersion: number, input: RoleInput): Promise<RoleItem>;
  retireRole(id: string, rowVersion: number): Promise<RoleItem>;
  users(): Promise<UserList>;
  createUser(input: NewUserInput): Promise<TemporaryPasswordResult>;
  updateUser(id: string, rowVersion: number, fullName: string): Promise<OfficeUserItem>;
  /** Giriş e-postasını düzeltir (ADR 0145): `users.edit` ve işlemi yapanın parolası. */
  changeEmail(
    id: string,
    rowVersion: number,
    email: string,
    password: string,
  ): Promise<OfficeUserItem>;
  /** Yönetici hesabına geçişte geçici parolası düşen kişi yanıtta (ADR 0150 §6). */
  setCompanyRoles(id: string, roles: DatedRoleInput[]): Promise<OfficeUserSaved>;
  /**
   * `expected`: çekmecenin yüklediği satırlar (ADR 0155 K7); arada değişen kümede 409
   * `ROW_VERSION_CONFLICT`, hiçbir şey yazılmaz. Verilmezse eski davranış.
   */
  setVesselAssignments(
    id: string,
    assignments: VesselAssignmentInput[],
    expected?: ReadonlyArray<{ id: string; rowVersion: number }>,
  ): Promise<OfficeUserSaved>;
  disableUser(id: string, rowVersion: number, password: string): Promise<OfficeUserItem>;
  enableUser(id: string, rowVersion: number, password: string): Promise<OfficeUserItem>;
  resetPassword(id: string, rowVersion: number, password: string): Promise<TemporaryPasswordResult>;
  resetTotp(id: string, password: string): Promise<OfficeUserItem>;
  matrix(): Promise<MatrixData>;
  matrixVersions(): Promise<MatrixVersionItem[]>;
  matrixVersion(version: number): Promise<MatrixVersionData>;
  matrixDiff(from: number, to: number): Promise<MatrixDiffData>;
  matrixSubjects(): Promise<MatrixSubjects>;
  saveMatrix(input: SaveMatrixInput): Promise<MatrixSaved>;
  /** Çalışma kitabının adresi (indirme bağlantısı). */
  matrixExportUrl(version: number): string;
  importMatrix(file: Blob): Promise<MatrixImportPreview>;
}

/** Hata kodu: API hatasının `code`'u; bilinmeyen hata `GENERIC`. */
export function setupErrorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === 'string' && code !== '') return code;
  }
  return 'GENERIC';
}

/** Hata ayrıntısı (ör. `LAST_ADMIN` için `self`). */
export function setupErrorDetail(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return undefined;
}
